import { createNlsMiddleware, expandedTopicChange, keymap, lccstate, load_topic, pathmap, rolldownGotit, trackingWYLOChange, userstate } from 'lcc-state';
import has from '@dojo/framework/core/has';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { diffProperty, invalidator } from '@dojo/framework/core/vdom';
import { audio, AudioPlayer, PlayButton } from '@holmescorp/audio';
import { BackView } from '@holmescorp/backview';
import { delay } from '@holmescorp/common';
import { scroll } from '@holmescorp/middleware';
import { subscribe } from '@holmescorp/pub-sub';
import css from '../css/topicgroup.m.css';
import { BackToTop } from '../support/back-to-top';
import { NextTopic } from '../support/next-topic';
import { QuizButton } from '../support/quiz-button';
import { StudyplanButton } from '../support/studyplan-button';
import { handleReadingCompleted, handleReadingLoaded } from '../support/xapi';
const nls = createNlsMiddleware('ditabook');
const nls_settings = createNlsMiddleware('settings');
const icache = createICacheMiddleware();
const factory = create({
    icache,
    theme,
    nls,
    nls_settings,
    lccstate,
    userstate,
    audio,
    invalidator,
    diffProperty,
    scroll
}).properties();
export const TopicFull = factory(({ properties, middleware: { icache, theme, nls, nls_settings, lccstate, userstate, audio, invalidator, diffProperty, scroll } }) => {
    const { isPlaceholder, messages: { reading = '[ditabook] reading', tg_quiz_label: quizText = 'Quiz', back_to_top = '[ditabook] back_to_top' } } = nls();
    const { messages: { association = '[settings] association' } } = nls_settings();
    if (isPlaceholder) {
        return null;
    }
    subscribe('scroll-to-topic', (topic, topicId) => {
        scroll.scrollIntoView(topicId, 'title-block');
    });
    diffProperty('topic', (previous, next) => {
        if (previous.topic !== next.topic) {
            audio.teardown();
            icache.clear();
            window.scrollTo(0, 0);
            invalidator();
        }
    });
    const { get, path } = lccstate;
    const bookversion = get(path('bookversion'));
    const lang = window.hcstatic.portal_language || 'en';
    const { topic: { attributes = {}, children = [] } } = properties();
    const { key, title, rel = '' } = attributes;
    icache.getOrSet('parentTitle', async () => {
        await delay(50);
        const { get, path } = userstate;
        const location = get(path('location'));
        const [, , , topicset, ,] = location.split('/');
        try {
            const { path: keyPath } = keymap.get(topicset);
            const { topic } = pathmap.get(keyPath);
            let title;
            if (topic.topicset.attributes) {
                title = topic.topicset.attributes.title;
            }
            return title;
        }
        catch (error) {
            console.info(error);
        }
    });
    const parentTitle = icache.get('parentTitle');
    const overrideSelected = () => {
        const { get: uget, path: upath } = userstate;
        const location = uget(upath('location'));
        const [, explore, , topicset, topicgroup, topic] = location.split('/');
        const inExplore = explore !== undefined && explore === 'explore';
        const parent = inExplore ? (topic !== undefined ? topicgroup : null) : null;
        trackingWYLOChange({ tracking: {} });
        expandedTopicChange({ topicid: topicset });
        const view = parent !== null
            ? () => {
                const { path } = keymap.get(parent);
                return pathmap.get(path).view;
            }
            : inExplore
                ? () => pathmap.get('/explore').view
                : () => () => {
                    window.history.back();
                };
        view()();
    };
    const setPageTitle = () => {
        const product = get(path('product'));
        document.title = `${reading} - ${title} (${association} ${product.toLocaleUpperCase()} )`;
    };
    const themeCss = theme.classes(css);
    const playlist = [];
    if (has('use-audio-topics')) {
        playlist.push({ key, title });
        children
            .filter((val) => !('shortdesc' in val))
            .forEach((child) => {
            const { attributes = { rel: '', title: '' } } = child.topicref;
            if (attributes.rel && attributes.title) {
                playlist.push({ key: attributes.rel, title: attributes.title });
            }
        });
        audio.setup(playlist);
    }
    const audioWidget = has('use-audio-topics') && playlist ? w(AudioPlayer, { audio }) : null;
    const playWidget = (title, key, trackNumber) => {
        return audioWidget ? w(PlayButton, { title, key, trackNumber, audio }) : null;
    };
    const deck = () => {
        const { topic } = properties();
        return w('Generator', { skipTitle: true, topic });
    };
    const competency_connection = (rel) => {
        icache.set('comconnrel', rel);
        const comconn = icache.getOrSet('comconn', async () => {
            const val = (await load_topic(rel, bookversion, lang));
            return val;
        });
        const { children } = comconn ? comconn.topic : { children: undefined };
        const body = children ? children.filter((v) => 'body' in v) : [];
        const hasBody = body.length > 0;
        const titleTD = children?.[0]?.title;
        const titleChild = titleTD?.children?.[0];
        const title = typeof titleChild === 'string' ? titleChild : '';
        return v('div', { key: 'compconn', xqa: 'topicgroup compconn', classes: [themeCss.competencyConnection] }, [
            playWidget(title, 'compconn', 0),
            hasBody ? w('Generator', { skipTitle: true, renderBody: true, compconn: true, topic: comconn }) : null
        ]);
    };
    const topics = icache.getOrSet('topics', async () => {
        const kids = children.filter((val) => !('shortdesc' in val));
        const topics = [];
        for (const kid in kids) {
            const { attributes = {} } = kids[kid].topicref;
            const { rel: krel = '' } = attributes;
            const topic = (await load_topic(krel, bookversion, lang));
            topics.push(topic);
        }
        return topics;
    });
    const topic_content = topics
        ? topics.map((topic, index) => {
            const key = topic.topic.attributes.id;
            const titleTD = topic.topic.children[0].title;
            const titleChild = titleTD?.children?.[0];
            const title = typeof titleChild === 'string' ? titleChild : '';
            return v('div', {
                key: `${topic.topic.attributes.id}`,
                xqa: `topic ${topic.topic.attributes.id}`,
                classes: [themeCss.topicBlock]
            }, [
                playWidget(title, key, index + 1),
                w('Generator', {
                    key: `${topic.topic.attributes.id}-body`,
                    skipTitle: false,
                    renderBody: true,
                    topic: topic
                })
            ]);
        })
        : [];
    if (topics) {
        const parentTopic = { topic: { attributes, children } };
        const allTopics = [parentTopic, ...topics];
        icache.getOrSet('loaded', async () => {
            await handleReadingLoaded(allTopics);
            return true;
        });
        const lastElement = document.getElementById('topicgroup-actions');
        if (lastElement && !icache.get('observing')) {
            icache.set('observing', true);
            const readingObserver = new IntersectionObserver(async (entries, observer) => {
                if (icache.get('processingCompleted')) {
                    return;
                }
                if (entries[0].isIntersecting) {
                    icache.set('processingCompleted', true);
                    await handleReadingCompleted(allTopics);
                    observer.unobserve(entries[0].target);
                    const status = userstate.get(userstate.path('status'));
                    const data = { ...status[key].data };
                    data.complete = 1;
                    rolldownGotit(key, data);
                }
            }, { threshold: 1 });
            readingObserver.observe(lastElement);
        }
    }
    setPageTitle();
    const groupTitle = `${rel}-title`;
    if (topic_content && topic_content.length > 0) {
        const actionButton = (currentTopic) => {
            if (has('use-topicgroup-quiz')) {
                return w(QuizButton, { topicid: key, text: quizText, showscore: false });
            }
            const { get: uget, path: upath } = userstate;
            const status = uget(upath('status'));
            const { next } = status[currentTopic];
            if (next === null && has('use-study-plan')) {
                return w(StudyplanButton, {});
            }
            else if (next === null) {
                return null;
            }
            const { title } = keymap.get(next) || { title: '' };
            return w(NextTopic, { topicid: next, text: title });
        };
        return v('div', { key: `tgroot-${key}`, classes: [themeCss.root] }, [
            v('div', {
                id: groupTitle,
                xqa: `topicgroup grouphead`,
                classes: [themeCss.grouphead]
            }, [
                v('div', { classes: [themeCss.titleBlock], key: 'title-block', tabIndex: 0 }, [
                    v('h1', { classes: [themeCss.title] }, [title]),
                    v('div', {}),
                    w(BackView, {
                        overrideSelected,
                        overrideTitle: `${parentTitle} Home`
                    })
                ])
            ]),
            v('div', {
                role: 'group',
                'aria-labelledby': groupTitle,
                key,
                xqa: `topicgroup ${key}`,
                classes: [themeCss.group]
            }, [
                audioWidget,
                deck(),
                competency_connection(rel),
                ...topic_content,
                v('div', { classes: [themeCss.actionBlock] }, [
                    actionButton(key),
                    w(BackToTop, { text: back_to_top, topicid: key })
                ])
            ]),
            v('div', { id: 'topicgroup-actions', xqa: `topicgroup actions`, classes: [themeCss.actions] }),
            w('TocSlidePane', {})
        ]);
    }
    return null;
});
